
import { Vue, Component, Watch } from 'vue-property-decorator'
import {
	GeoJSONOptions,
	GeoJSONEvent,
	LatLng,
	LeafletMouseEvent,
	DivIcon
} from 'leaflet'
import {
	LMap,
	LTileLayer,
	LGeoJson,
	LControl,
	LPolygon,
	LMarker
} from 'vue2-leaflet'
import CovJsonLayer from './CovJsonLayer.vue'
import { MutationTypes } from '@/store/mutations'
import { disasterColorFunc, filterFunc } from '@/lib/common'
import { DisasterDatum } from '@/lib/types'
import { turboPalette, divergingPalette } from '@/lib/palettes'
import { format } from 'date-fns'
import * as CovJSON from 'covjson-reader'

@Component({
	name: 'Map',
	components: {
		LMap,
		LTileLayer,
		LGeoJson,
		LControl,
		LPolygon,
		LMarker,
		CovJsonLayer
	}
})
export default class Map extends Vue {
	readonly bounds: LatLng[] = [new LatLng(-90, -180), new LatLng(90, 180)]
	readonly turboPalette = turboPalette
	readonly divergingPalette = divergingPalette

	get showRainfall() {
		return this.$tStore.state.showRainfall
	}

	set showRainfall(rainfall: boolean) {
		this.$tStore.commit(MutationTypes.SET_SHOW_RAINFALL, rainfall)
	}

	toggleRainfall() {
		this.showRainfall = !this.showRainfall
	}

	get selectedFeature() {
		return this.$tStore.state.selectedFeature
	}

	get disasters() {
		if (
			!this.selectedFeature ||
			this.$tStore.state.disasterData === null ||
			this.$tStore.state.georefData === null
		) {
			return []
		}
		// @ts-ignore - it can't be null, we've checked above
		const time = this.selectedFeature.properties.date.getTime()
		if (this.$tStore.state.date2DisasterIndex.has(time)) {
			const disasterIndices = this.$tStore.state.date2DisasterIndex.get(time)
			const ret: any = []
			// @ts-ignore - it can't be undefined, that's what the .has() check is for
			disasterIndices.forEach(i => {
				// @ts-ignore - it can't be null, we've checked above
				const d = this.$tStore.state.disasterData[i]
				if (filterFunc(d, this.$tStore.state.disasterFilter)) {
					ret.push({
						...d,
						// @ts-ignore - it can't be null, we've checked above
						latLng: this.$tStore.state.georefData[
							// @ts-ignore - it can't be null, we've checked above
							d.mCode
						].latLng,
						// @ts-ignore - it can't be null, we've checked above
						name: this.$tStore.state.georefData[
							// @ts-ignore - it can't be null, we've checked above
							d.mCode
						].name
					})
				}
			})
			return ret
		}
		return []
	}

	get seasonFeatures() {
		if (
			this.$tStore.state.selectedSeasonIndex !== null &&
			this.$tStore.state.cloudData !== null
		) {
			const startDate = new Date(
				1979 + this.$tStore.state.selectedSeasonIndex,
				8,
				1
			)
			const endDate = new Date(
				1980 + this.$tStore.state.selectedSeasonIndex,
				3,
				30
			)
			// @ts-ignore
			return this.$tStore.state.cloudData.features.filter(
				(f: any) =>
					f.features[0].properties.date.getTime() > startDate.getTime() &&
					f.features[0].properties.date.getTime() < endDate.getTime()
			)
		}
		return []
	}

	get bgLayer() {
		return {
			name: 'Stadia OSM Bright',
			url: 'https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png',
			attribution:
				'&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
		}
	}

	get gradientStr() {
		const gradSteps = []
		for (let i = 0; i < this.turboPalette.steps; i++) {
			gradSteps.push(
				`rgb(${this.turboPalette.red[i]}, ${this.turboPalette.green[i]}, ${this.turboPalette.blue[i]})`
			)
		}
		return gradSteps.join(',')
	}

	mounted() {
		this.getRainfallData()
	}

	rainfallCovjson: any = null

	@Watch('$tStore.state.selectedEvent')
	async getRainfallData() {
		this.$store.commit('SET_LOADING')
		const dateStr = format(
			// @ts-ignore
			this.$tStore.state.selectedEvent?.features[0].properties.date,
			'yyyy-MM-dd'
		)
		const rainfallUrl =
			process.env.NODE_ENV === 'production'
				? `${window.location.origin}/vp-cloud-band-explorer/data/rainfall_${dateStr}.covjson`
				: `${window.location.origin}/data/rainfall_${dateStr}.covjson`

		this.rainfallCovjson = await CovJSON.read(rainfallUrl)
		this.$store.commit('SET_FINISHED_LOADING')
	}

	get selectedTime() {
		// @ts-ignore
		return this.selectedFeature?.properties.date
	}

	getIcon(disaster: DisasterDatum) {
		const c = disasterColorFunc(disaster)
		if (c === null) {
			return null
		}
		if (disaster.major) {
			const markerHtmlStyles = `
			background-color: rgb(${c.r},${c.g},${c.b});
			width: 30px;
			height: 30px;
			display: block;
			left: -15px;
			top: -15px;
			position: relative;
			border-radius: 20px 20px 0;
			transform: rotate(45deg);
			border: 2px dotted #fff`

			return new DivIcon({
				className: 'my-custom-pin',
				iconAnchor: [0, 24],
				popupAnchor: [0, -32],
				html: `<span style="${markerHtmlStyles}" />`
			})
		} else {
			const markerHtmlStyles = `
			background-color: rgb(${c.r},${c.g},${c.b});
			width: 20px;
			height: 20px;
			display: block;
			left: -10px;
			top: -10px;
			position: relative;
			border-radius: 20px 20px 0;
			transform: rotate(45deg);
			border: 1px solid #FFFFFF`

			return new DivIcon({
				className: 'my-custom-pin',
				iconAnchor: [0, 24],
				popupAnchor: [0, -32],
				html: `<span style="${markerHtmlStyles}" />`
			})
		}
	}

	regionStyle(): GeoJSONOptions {
		if (this.$tStore.getters.loading) {
			return {
				style: {
					weight: 0,
					fillOpacity: 0
				}
			}
		}
		return {
			style: {
				color: this.$tStore.state.color,
				fillColor: this.$tStore.state.color,
				weight: 3,
				fillOpacity: this.showRainfall ? 0 : 0.75,
				className: 'cloud-blob'
			}
		}
	}

	seasonStyle(event: GeoJSONEvent): GeoJSONOptions {
		if (this.$tStore.getters.loading) {
			return {
				style: {
					weight: 0,
					fillOpacity: 0
				}
			}
		}
		return {
			style: {
				weight: 0,
				fillColor: '#000000',
				fillOpacity: Math.log(this.seasonFeatures.length) / 250,
				className: 'cloud-blob'
			}
		}
	}

	addPopup(event: LeafletMouseEvent, marker: any) {
		if (marker.major) {
			const d = marker.major
			let popupText = `<h3>${d.title[this.$lang]}</h3>`
			for (let p of d.content[this.$lang]) {
				popupText += `<p>${p}</p>`
			}
			if (d.links)
				for (let l of d.links) {
					if (Object.prototype.hasOwnProperty.call(l, this.$lang)) {
						popupText += `<p><a href="${l[this.$lang].link}" target="_blank">${
							l[this.$lang].title
						}</a></p>`
					} else {
						popupText += `<p><a href="${l.link}" target="_blank">${
							l.title[this.$lang]
						}</a></p>`
					}
				}
			if (d.images)
				for (let i of d.images) {
					popupText += `<div class="image"><img src="${
						i.link
					}" height="240px"><p>${i.title[this.$lang]}</p></div>`
				}
			event.target.bindPopup(popupText, { autoPan: true, maxWidth: 500 })
		} else {
			console.log('about to throw a fit?', marker)
			event.target.bindPopup(
				`<h3>${marker.name}</h3><p>${marker.type[this.$lang]}</p>`,
				{ autoPan: true, maxWidth: 300 }
			)
		}
	}
}
